<script lang="ts" setup>
import { ButtonComponent, Card, UploadInput } from '@metapic-apps/shared'
import type { UploadRawFile } from 'element-plus'
import { ElNotification } from 'element-plus'
import { computed, ref } from 'vue'

import { useUploadAdvertiserLogo } from '@/api/advertiser.api'
import type { ProcessableImageType } from '@/types/common.types'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'

const props = defineProps<{
  advertiserId: string
  logoUrl?: string
}>()

const imageThumbnail = ref<UploadRawFile | Blob>()

const currentLogo = computed((): ProcessableImageType | undefined => {
  if (!props.logoUrl) {
    return undefined
  }
  return { key: '', baseUrl: '', url: props.logoUrl }
})

const { loading, error, uploadLogo } = useUploadAdvertiserLogo(computed(() => props.advertiserId))

const handleUploadLogo = async () => {
  if (!imageThumbnail.value) {
    return
  }
  await uploadLogo(imageThumbnail.value)
  if (!error.value) {
    ElNotification.success('Logo uploaded successfully')
  }
  imageThumbnail.value = undefined
}
</script>

<template>
  <Card title="Logo" title-size="small" class="logo-settings">
    <BoxComponent :max-w="600" class="logo-settings__upload-container">
      <UploadInput
        v-model:image-thumbnail="imageThumbnail"
        :image="currentLogo"
        :maximum-size="0.5"
        class="logo-settings__upload-input"
        :show-cropper="false"
        :auto-upload="false"
      >
        <template #placeholder> Must be JPG or PNG, maximum file size 500KB. </template>
      </UploadInput>
    </BoxComponent>
    <template #footer>
      <ButtonComponent
        variant="accent"
        :is-loading="loading"
        :is-disabled="!imageThumbnail"
        class="logo-settings__button"
        @click="handleUploadLogo"
      >
        Save
      </ButtonComponent>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.logo-settings {
  &__button {
    align-self: flex-start;
  }

  &__upload-container {
    align-self: center;
  }
  :deep(.upload-input__wrapper) {
    gap: 48px;
  }

  :deep(.el-upload-dragger) {
    border: none;
  }

  :deep(.upload-input__thumbnail) {
    object-fit: contain;
  }
}
</style>
